// ======================================================
// NK REFINERY
// EQUIPMENT ENGINE
// ======================================================

import { useContext, useMemo } from "react";

import { PumpContext } from "../context/PumpContext";
import { ProcessContext } from "../context/ProcessContext";
import { AirCoolerContext } from "../context/AirCoolerContext";

export default function EquipmentEngine() {

    const { pumpData } = useContext(PumpContext);

    const { processData } = useContext(ProcessContext);
    const { coolerData } = useContext(AirCoolerContext);



/*====================================================
  PUMPS
====================================================*/

const ELECTRICITY_COST = 0.18; // USD per kWh (editable later)

const pumps = (() => {

    const list = Object.entries(pumpData || {}).map(([tag, pump]) => {

        const running = Boolean(pump?.running);

        const rpm = Number(pump?.rpm || 0);

        const designRPM = Number(pump?.designRPM || 1800);

        const motorKW = Number(pump?.motorKW || 55);

        const utilization =
            designRPM > 0
                ? Math.min((rpm / designRPM) * 100, 100)
                : 0;

        const powerKW =
            running
                ? motorKW * (utilization / 100)
                : 0;

        const dailyEnergy =
            powerKW * 24;

        const dailyCost =
            dailyEnergy * ELECTRICITY_COST;

        const maintenanceCost =
            running
                ? motorKW * 0.40
                : 0;

        const health =
            running
                ? Math.max(70, 100 - utilization * 0.15)
                : 100;

        return {

            tag,

            running,

            rpm,

            designRPM,

            utilization,

            motorKW,

            powerKW,

            dailyEnergy,

            dailyCost,

            maintenanceCost,

            health

        };

    });

    return {

        list,

        total: list.length,

        running:

            list.filter(p => p.running).length,

        stopped:

            list.filter(p => !p.running).length,

        totalPower:

            list.reduce((a, b) => a + b.powerKW, 0),

        totalEnergy:

            list.reduce((a, b) => a + b.dailyEnergy, 0),

        totalCost:

            list.reduce((a, b) => a + b.dailyCost, 0),

        maintenance:

            list.reduce((a, b) => a + b.maintenanceCost, 0),

        averageHealth:

            list.length
                ? list.reduce((a, b) => a + b.health, 0) / list.length
                : 100

    };

})();


    /*====================================================
      FURNACES
    ====================================================*/

    /*====================================================
  FURNACES
====================================================*/

const furnaces = {


    "F-101": {


        tag:"F-101",

        running:
            Boolean(
                processData?.pilotOn
            ),


        flame:

            processData?.pilotOn
            ?
            "ON"
            :
            "OFF",



        spark:

            Boolean(
                processData?.spark
            ),



        crudeFeed:

            Number(
                processData?.FIC_2101 || 0
            ),



        outletTemperature:

            Number(
                processData?.TT_2102 || 0
            ),



        air:

            Number(
                processData?.ATM_Air || 0
            ),



        damper:

            Number(
                processData?.Damper_Opening || 0
            ),



        fuelEfficiency:

            processData?.pilotOn
            ?
            Math.min(
                85 +
                (
                Number(processData?.TT_2102 || 0)
                /100
                ),
                95
            )
            :
            0,



        operatingCost:


            processData?.pilotOn
            ?
            250
            :
            0,



        health:


            processData?.pilotOn
            ?
            95
            :
            100



    }


};

    /*====================================================
      DISTILLATION TOWERS
    ====================================================*/

    /*====================================================
  DISTILLATION TOWERS
====================================================*/

const towers = {


    /* =================================================
       PRE-FLASH TOWER
    ================================================= */

    "PRE-FLASH TOWER": {


        tag:
            "V-2001",


        type:
            "PreFlash Tower",


        pressure:

            Number(
                processData?.PT_2006 || 0
            ),


        topTemperature:

            Number(
                processData?.TT_2006 || 0
            ),


        bottomTemperature:

            Number(
                processData?.TT_2007 || 0
            ),


        feedRate:

            Number(
                processData?.FIC_2001 || 0
            ),


        status:

            Number(processData?.FIC_2001 || 0) > 0
            ?
            "RUNNING"
            :
            "STOPPED",



        health:

            Number(processData?.FIC_2001 || 0) > 0
            ?
            95
            :
            100



    },



    /* =================================================
       MAIN FRACTIONATION TOWER
    ================================================= */

    "MAIN FRACTIONATION TOWER": {


        tag:
            "T-2201",


        type:
            "Atmospheric Distillation Tower",



        pressure:

            Number(
                processData?.PT_2201 || 0
            ),



        topTemperature:

            Number(
                processData?.TT_2201 || 0
            ),



        upperTemperature:

            Number(
                processData?.TT_2202 || 0
            ),



        middleTemperature:

            Number(
                processData?.TT_2203 || 0
            ),



        bottomTemperature:

            Number(
                processData?.TT_2303 || 0
            ),



        feedRate:

            Number(
                processData?.FIC_2301 || 0
            ),



        sideDraws:{


            kerosene:

                Number(
                    processData?.FIC_2305 || 0
                ),



            diesel:

                Number(
                    processData?.FIC_2306 || 0
                )

        },



        status:

            Number(processData?.FIC_2301 || 0) > 0
            ?
            "RUNNING"
            :
            "STOPPED",



        health:

            Number(processData?.FIC_2301 || 0) > 0
            ?
            96
            :
            100



    },



    /* =================================================
       STABILIZER TOWER
    ================================================= */

    "STABILIZER TOWER": {


        tag:
            "T-2901",


        type:
            "Naphtha Stabilizer",



        pressure:

            Number(
                processData?.PT_2901 || 0
            ),



        temperature:

            Number(
                processData?.TT_2901 || 0
            ),



        overheadFlow:

            Number(
                processData?.FIC_2905 || 0
            ),



        refluxFlow:

            Number(
                processData?.FIC_2901 || 0
            ),



        status:


            Number(processData?.FIC_2905 || 0) > 0
            ?
            "RUNNING"
            :
            "STOPPED",



        health:


            Number(processData?.FIC_2905 || 0) > 0
            ?
            97
            :
            100



    }



};

    /*====================================================
      HEAT EXCHANGERS
    ====================================================*/

    /*====================================================
  HEAT EXCHANGERS
====================================================*/

const exchangers = {


/* ===================================================
   E-101 A/B
   CRUDE PREHEATING TRAIN
=================================================== */

"E-101A": {


    tag:"E-101A",

    service:
        "Crude / Hot Product Heat Exchanger",


    crudeIn:

        Number(
            processData?.TT_14005 || 0
        ),


    crudeOut:

        Number(
            processData?.TT_14006 || 0
        ),



    hotStreamIn:

        Number(
            processData?.TT_14007 || 0
        ),


    hotStreamOut:

        Number(
            processData?.TT_14008 || 0
        ),



    temperatureApproach:


        Math.abs(

            Number(processData?.TT_14008 || 0)

            -

            Number(processData?.TT_14006 || 0)

        ),



    efficiency:


        85,



    dutyStatus:

        Number(processData?.FIC_1901 || 0) > 0
        ?
        "ONLINE"
        :
        "OFFLINE",



    health:95


},





"E-101B": {


    tag:"E-101B",

    service:
        "Crude Preheater",


    crudeIn:

        Number(
            processData?.TT_14006 || 0
        ),


    crudeOut:

        Number(
            processData?.TT_14009 || 0
        ),



    hotStreamIn:

        Number(
            processData?.TT_14010 || 0
        ),



    hotStreamOut:

        Number(
            processData?.TT_14008 || 0
        ),



    efficiency:88,


    health:94



},





/* ===================================================
   E-301 A/B
   NAPHTHA STABILIZER HEAT RECOVERY
=================================================== */


"E-301A": {


    tag:"E-301A",

    service:
        "Cold Naphtha / Stabilized Naphtha",


    coldIn:

        Number(
            processData?.TT_2707 || 0
        ),


    coldOut:

        Number(
            processData?.TT_2708 || 0
        ),



    hotIn:

        Number(
            processData?.TT_2709 || 0
        ),



    hotOut:

        Number(
            processData?.TT_27010 || 0
        ),



    temperatureDrop:


        Number(processData?.TT_2709 || 0)

        -

        Number(processData?.TT_27010 || 0),



    efficiency:90,


    health:96



},





"E-301B": {


    tag:"E-301B",

    service:
        "Naphtha Heat Recovery",



    coldIn:

        Number(
            processData?.TT_2707 || 0
        ),



    coldOut:

        Number(
            processData?.TT_2708 || 0
        ),



    efficiency:90,


    health:96



},





/* ===================================================
   E-117 A/B
   ATM RESIDUE COOLING
=================================================== */


"E-117A": {


    tag:"E-117A",

    service:
        "Atmospheric Residue Cooler",


    residueIn:

        Number(
            processData?.TT_3307 || 0
        ),


    residueOut:

        Number(
            processData?.TT_3309 || 0
        ),



    coolingWaterIn:

        Number(
            processData?.TT_3306 || 0
        ),



    coolingWaterOut:

        Number(
            processData?.TT_3308 || 0
        ),



    temperatureLoss:


        Number(processData?.TT_3307 || 0)

        -

        Number(processData?.TT_3309 || 0),



    efficiency:92,


    health:97


},




"E-117B": {


    tag:"E-117B",

    service:
        "Residue Cooling Second Stage",


    efficiency:90,


    health:95


}



};

    /*====================================================
  AIR COOLERS
====================================================*/


const AIR_ELECTRICITY_COST = 0.18;


const airCoolers = (()=>{


const list = Object.entries(
    coolerData || {}
)
.map(([tag,cooler])=>{


const running =
Boolean(
    cooler?.running
);


const rpm =
Number(
    cooler?.rpm || 0
);


const designRPM =
Number(
    cooler?.designRPM || 1900
);



const motorKW =
Number(
    cooler?.motorKW || 15
);



const utilization =
designRPM > 0
?
Math.min(
(rpm/designRPM)*100,
100
)
:
0;



const powerKW =
running
?
motorKW*(utilization/100)
:
0;



const dailyEnergy =
powerKW*24;



const dailyCost =
dailyEnergy*
AIR_ELECTRICITY_COST;



const health =
running
?
Math.max(
70,
100-(utilization*0.12)
)
:
100;



return {


tag,


running,


rpm,


designRPM,


utilization,


motorKW,


powerKW,


dailyEnergy,


dailyCost,


health


};


});



return {


list,


total:
list.length,


running:
list.filter(
x=>x.running
).length,


stopped:
list.filter(
x=>!x.running
).length,


totalPower:
list.reduce(
(a,b)=>a+b.powerKW,
0
),


totalEnergy:
list.reduce(
(a,b)=>a+b.dailyEnergy,
0
),


totalCost:
list.reduce(
(a,b)=>a+b.dailyCost,
0
),


averageHealth:
list.length
?
list.reduce(
(a,b)=>a+b.health,
0
)
/list.length
:
100


};



})();



    /*====================================================
  REBOILERS
====================================================*/

const reboilers = {


/* ===================================================
   STABILIZER TOWER REBOILER
=================================================== */

"RB-301": {


    tag:"RB-301",

    service:
        "Stabilizer Tower Bottom Reboiler",



    type:
        "Kettle Reboiler",



    /* Bottom temperature */

    bottomTemperature:

        Number(
            processData?.TT_3006 || 0
        ),



    /* Steam / heating medium indicator */

    heatingDuty:


        Number(
            processData?.FIC_3001 || 0
        ),



    operatingStatus:


        Number(
            processData?.FIC_3001 || 0
        ) > 0

        ?

        "ONLINE"

        :

        "OFFLINE",




    duty:


        Number(
            processData?.FIC_3001 || 0
        )

        *

        2257,



    efficiency:

        88,



    foulingFactor:

        0.00025,



    health:

        95



},




/* ===================================================
   MAIN FRACTIONATION TOWER REBOILER
=================================================== */


"RB-201": {


    tag:"RB-201",

    service:
        "Main Fractionation Tower Bottom Reboiler",



    type:

        "Steam Kettle Reboiler",



    bottomTemperature:


        Number(
            processData?.TT_2303 || 0
        ),



    steamFlow:


        Number(
            processData?.FIC_2305 || 0
        ),



    operatingStatus:


        Number(
            processData?.FIC_2305 || 0
        )

        >

        0

        ?

        "ONLINE"

        :

        "OFFLINE",




    duty:


        Number(
            processData?.FIC_2305 || 0
        )

        *

        2257,



    efficiency:


        90,



    foulingFactor:

        0.00020,



    health:

        96



},




/* ===================================================
   PREFLASH TOWER REBOILER (IF INSTALLED)
=================================================== */


"RB-101": {


    tag:"RB-101",

    service:
        "Preflash Tower Reboiler",



    type:

        "Steam Reboiler",



    bottomTemperature:


        Number(
            processData?.TT_2007 || 0
        ),



    heatingDuty:


        Number(
            processData?.FIC_2105 || 0
        ),



    operatingStatus:


        Number(
            processData?.FIC_2105 || 0
        )

        >

        0

        ?

        "ONLINE"

        :

        "OFFLINE",



    efficiency:

        85,



    foulingFactor:

        0.00030,



    health:

        92



}





};




    /*====================================================
  CONDENSERS
====================================================*/

const condensers = {


/* ===================================================
   MAIN FRACTIONATION OVERHEAD CONDENSER
=================================================== */

"COND-2201": {


    tag:"COND-2201",

    service:
        "Main Fractionation Tower Overhead Condenser",



    type:

        "Shell & Tube Condenser",



    inletTemperature:


        Number(
            processData?.TT_2201 || 0
        ),



    outletTemperature:


        Number(
            processData?.TT_2203 || 0
        ),



    temperatureDrop:


        Number(processData?.TT_2201 || 0)

        -

        Number(processData?.TT_2203 || 0),



    coolingFlow:


        Number(
            processData?.FIC_2203 || 0
        ),



    operatingStatus:


        Number(processData?.FIC_2203 || 0)

        >

        0

        ?

        "ONLINE"

        :

        "OFFLINE",



    duty:


        Number(processData?.FIC_2203 || 0)

        *

        4200,



    efficiency:

        92,



    foulingFactor:

        0.00015,



    health:

        95



},




/* ===================================================
   STABILIZER OVERHEAD CONDENSER
=================================================== */

"COND-2901": {


    tag:"COND-2901",

    service:

        "Stabilizer Tower Overhead Condenser",



    type:

        "Air Cooler / Condenser",



    inletTemperature:


        Number(
            processData?.TT_2901 || 0
        ),



    outletTemperature:


        Number(
            processData?.TT_2907 || 0
        ),



    temperatureDrop:


        Number(processData?.TT_2901 || 0)

        -

        Number(processData?.TT_2907 || 0),



    coolingFlow:


        Number(
            processData?.FIC_2905 || 0
        ),



    operatingStatus:


        Number(processData?.FIC_2905 || 0)

        >

        0

        ?

        "ONLINE"

        :

        "OFFLINE",




    duty:


        Number(processData?.FIC_2905 || 0)

        *

        3500,



    efficiency:

        90,



    foulingFactor:

        0.00018,



    health:

        94



},




/* ===================================================
   PREFLASH OVERHEAD CONDENSER
=================================================== */

"COND-2001": {


    tag:"COND-2001",

    service:

        "Preflash Tower Overhead Condenser",



    type:

        "Shell & Tube Condenser",



    inletTemperature:


        Number(
            processData?.TT_2006 || 0
        ),



    outletTemperature:


        Number(
            processData?.TT_2007 || 0
        ),



    temperatureDrop:


        Number(processData?.TT_2006 || 0)

        -

        Number(processData?.TT_2007 || 0),



    coolingFlow:


        Number(
            processData?.FIC_2001 || 0
        ),



    operatingStatus:


        Number(processData?.FIC_2001 || 0)

        >

        0

        ?

        "ONLINE"

        :

        "OFFLINE",




    duty:


        Number(processData?.FIC_2001 || 0)

        *

        4000,



    efficiency:

        88,



    foulingFactor:

        0.00022,



    health:

        92



}





};

    /*====================================================
      STORAGE TANKS
    ====================================================*/

    /*====================================================
  STORAGE TANKS
====================================================*/


const tanks = {


/* =====================================================
   CRUDE STORAGE TANKS
===================================================== */


"V-1301": {

    tag:"V-1301",

    type:"Crude Storage Tank",

    service:"Crude Oil Storage",

    capacity:25000000,

    level:
        Number(
            processData?.LT_1301 || 0
        ),


    inventory:

        Number(
            processData?.LT_1301 || 0
        )
        *
        25000000
        /
        100,


    status:

        Number(processData?.LT_1301 || 0)

        > 5

        ?

        "AVAILABLE"

        :

        "LOW",


    health:98

},



"V-1302": {

    tag:"V-1302",

    type:"Crude Storage Tank",

    service:"Crude Oil Storage",

    capacity:25000000,

    level:
        Number(
            processData?.LT_1302 || 0
        ),


    inventory:

        Number(
            processData?.LT_1302 || 0
        )
        *
        25000000
        /
        100,


    status:"AVAILABLE",

    health:98

},



"V-1303": {

    tag:"V-1303",

    type:"Crude Storage Tank",

    service:"Crude Oil Storage",

    capacity:25000000,

    level:
        Number(
            processData?.LT_1303 || 0
        ),


    inventory:

        Number(
            processData?.LT_1303 || 0
        )
        *
        25000000
        /
        100,


    status:"AVAILABLE",

    health:98

},



"V-1304": {

    tag:"V-1304",

    type:"Crude Storage Tank",

    service:"Crude Oil Storage",

    capacity:25000000,

    level:
        Number(
            processData?.LT_1304 || 0
        ),


    inventory:

        Number(
            processData?.LT_1304 || 0
        )
        *
        25000000
        /
        100,


    status:"AVAILABLE",

    health:98

},





/* =====================================================
   BLENDING TANKS
===================================================== */


"BT-01": {

    tag:"BT-01",

    type:"Blending Tank",

    service:"Crude/Product Blending",

    capacity:10000000,

    level:
        Number(
            processData?.LT_BT01 || 0
        ),

    inventory:
        Number(
            processData?.LT_BT01 || 0
        )
        *
        10000000
        /
        100,


    status:"AVAILABLE",

    health:95

},



"BT-02": {

    tag:"BT-02",

    type:"Blending Tank",

    service:"Crude/Product Blending",

    capacity:10000000,


    level:
        Number(
            processData?.LT_BT02 || 0
        ),


    inventory:
        Number(
            processData?.LT_BT02 || 0
        )
        *
        10000000
        /
        100,


    status:"AVAILABLE",

    health:95

},




/* =====================================================
   DAY TANKS
===================================================== */


"DAY-TANK-01": {

    tag:"DAY-TANK-01",

    type:"Day Tank",

    service:"Intermediate Product Buffer",

    capacity:5000000,


    level:
        Number(
            processData?.LT_DAY01 || 0
        ),


    inventory:
        Number(
            processData?.LT_DAY01 || 0
        )
        *
        5000000
        /
        100,


    status:"ONLINE",

    health:96

},



"DAY-TANK-02": {

    tag:"DAY-TANK-02",

    type:"Day Tank",

    service:"Intermediate Product Buffer",

    capacity:5000000,


    level:
        Number(
            processData?.LT_DAY02 || 0
        ),


    inventory:
        Number(
            processData?.LT_DAY02 || 0
        )
        *
        5000000
        /
        100,


    status:"ONLINE",

    health:96

},





/* =====================================================
   SLOP TANK
===================================================== */


"SLOP-TANK-01": {


    tag:"SLOP-TANK-01",

    type:"Slop Tank",

    service:"Off-Spec Product Recovery",


    capacity:10000000,


    level:
        Number(
            processData?.LT_SLOP01 || 0
        ),


    inventory:
        Number(
            processData?.LT_SLOP01 || 0
        )
        *
        10000000
        /
        100,


    status:

        "RECOVERY",


    health:90

}




};

    /*====================================================
      VALVES
    ====================================================*/

    /*====================================================
  VALVES
====================================================*/


const valves = (() => {


    /*
       All shutdown valves are stored as:
       
       XVxxxx

       Example:
       XV1702
       XV1703
       XV1705
       XV1706
       XV1707
       XV1708
       XV1801
       XV1802

    */


    const valveTags = [

        "XV1702",
        "XV1703",
        "XV1705",
        "XV1706",
        "XV1707",
        "XV1708",

        "XV1801",
        "XV1802",


        // Loading valves
        "XV1001",
        "XV1002",


        // Add more later

    ];



    const list =
        valveTags.map(tag => {


            const position =
                Number(
                    localStorage.getItem(tag)
                )
                || 0;



            const open =
                position === 1;



            return {


                tag,


                type:
                    "Shutdown Valve",



                position:
                    open
                    ?
                    "OPEN"
                    :
                    "CLOSED",



                status:
                    open
                    ?
                    "AVAILABLE"
                    :
                    "ISOLATED",



                availability:

                    open
                    ?
                    100
                    :
                    0,



                maintenancePriority:

                    open
                    ?
                    "LOW"
                    :
                    "HIGH",



                health:

                    open
                    ?
                    95
                    :
                    80



            };


        });




    return {


        list,


        total:
            list.length,



        open:

            list.filter(
                v =>
                v.position === "OPEN"
            ).length,



        closed:

            list.filter(
                v =>
                v.position === "CLOSED"
            ).length,



        availability:


            list.length

            ?

            (

            list.filter(
                v =>
                v.position === "OPEN"
            )
            .length

            /

            list.length

            )

            *100

            :

            100,



        maintenance:


            list.filter(

                v =>
                v.maintenancePriority === "HIGH"

            )

    };

})();


}